import React from 'react';

export interface CsatBlock {
  rating: number | null;
  comment?: string | null;
  requestedAt: string | null;
  respondedAt: string | null;
}

/**
 * Compact CSAT plaque for a ticket detail card.
 *
 * Three states:
 *   - `requestedAt=null`         → render nothing (we haven't asked yet)
 *   - `rating=null`              → "Ждём оценку клиента…"
 *   - `rating=1..5`              → ⭐⭐⭐☆☆ + score label
 *
 * Kept presentation-only: no fetching, no mutation. The page passes ticket.csat.
 */
const CsatBadge: React.FC<{ csat: CsatBlock | null | undefined }> = ({ csat }) => {
  if (!csat || !csat.requestedAt) return null;

  if (csat.rating == null) {
    return (
      <div className="mt-3 p-3 rounded-lg border border-border bg-card/60 text-sm text-muted-foreground">
        💬 Опрос CSAT отправлен клиенту — ждём оценку.
      </div>
    );
  }

  const r = Math.max(1, Math.min(5, csat.rating));
  const tone =
    r >= 4 ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/10'
    : r === 3 ? 'text-amber-500 border-amber-500/30 bg-amber-500/10'
    : 'text-rose-500 border-rose-500/30 bg-rose-500/10';

  return (
    <div className={`mt-3 p-3 rounded-lg border ${tone}`}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <span className="text-base tracking-wide">
          {'⭐'.repeat(r)}
          <span className="opacity-30">{'⭐'.repeat(5 - r)}</span>
        </span>
        <span>Оценка клиента: {r}/5</span>
      </div>
      {csat.comment && (
        <p className="mt-2 text-xs text-foreground/80 whitespace-pre-wrap">«{csat.comment}»</p>
      )}
    </div>
  );
};

export default CsatBadge;
